import React, { useEffect, useState } from "react";

import { ReloadOutlined } from "@ant-design/icons";
import { Button, message, Tooltip } from "antd";
import moment from "moment";
import PropTypes from "prop-types";

import {
  getCreatorEarningsUpdateStatus,
  triggerCreatorEarningsUpdate,
} from "../../Pages/api";
import { DATE_TIME_FORMAT } from "../../Utils/constants";

const formatDate = (date) =>
  date ? moment(date).format(DATE_TIME_FORMAT) : "-";

const formatDuration = (fromDate, toDate) => {
  const diffMinutes = Math.max(
    0,
    Math.round(moment(toDate).diff(moment(fromDate), "minutes"))
  );
  const hours = Math.floor(diffMinutes / 60);
  const minutes = diffMinutes % 60;

  if (hours === 0) {
    return `${minutes} min${minutes === 1 ? "" : "s"}`;
  }
  if (minutes === 0) {
    return `${hours} hr${hours === 1 ? "" : "s"}`;
  }
  return `${hours} hr${hours === 1 ? "" : "s"} ${minutes} min${
    minutes === 1 ? "" : "s"
  }`;
};

const RefreshEarningsButton = ({ style = {} }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchStatus = async () => {
    try {
      const resp = await getCreatorEarningsUpdateStatus();
      setStatus(resp?.result || null);
      return resp?.result || null;
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleClick = async () => {
    setLoading(true);
    try {
      // re-check status at click time in case the UI is stale
      const latestStatus = await fetchStatus();
      if (latestStatus?.isRunning) {
        message.warning("Earnings refresh is already running");
        return;
      }

      await triggerCreatorEarningsUpdate();
      message.success("Earnings refresh started");
      await fetchStatus();
    } catch (error) {
      message.error("Failed to trigger earnings refresh. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const isRunning = Boolean(status?.isRunning);

  const getTooltipTitle = () => {
    if (isRunning) {
      return `Currently running since ${formatDate(status?.lastRunAt)}`;
    }
    if (!status?.lastRunAt) {
      return "No refresh has run yet.";
    }
    const now = moment();
    const lastUpdatedText = `Last updated ${formatDuration(
      status.lastRunAt,
      now
    )} ago.`;

    if (!status?.nextRunAt || moment(status.nextRunAt).isSameOrBefore(now)) {
      return `${lastUpdatedText} Next refresh is due.`;
    }

    return `${lastUpdatedText} Next refresh will be after ${formatDuration(
      now,
      status.nextRunAt
    )}.`;
  };

  const tooltipTitle = getTooltipTitle();

  return (
    <Tooltip title={tooltipTitle} overlayStyle={{ maxWidth: 320 }}>
      <Button
        className="siteBtn grayBtn"
        onClick={handleClick}
        loading={loading}
        disabled={isRunning}
        style={style}
      >
        <ReloadOutlined />
        Refresh Earnings
      </Button>
    </Tooltip>
  );
};

RefreshEarningsButton.propTypes = {
  style: PropTypes.shape(),
};

export default RefreshEarningsButton;
